import { Modal } from '@dynamika-shared/ui/Modal/Modal';
import { TextArea } from '@dynamika-shared/ui/TextArea/TextArea';
import { Attention } from '@dynamika-shared/ui/icons';
import { twMerge } from '@dynamika-shared/utils/twMerge';
import React, { useEffect, useState } from 'react';

export type ValidatedActionModalConfirmData = {
  comment: string;
};

type ValidatedActionModalProps = {
  open: boolean;
  title: string;
  errors: string[];
  attentionTitle?: string;
  commentLabel?: string;
  commentPlaceholder?: string;
  confirmText?: string;
  cancelText?: string;
  validating?: boolean;
  disableConfirm?: boolean;
  submitting?: boolean;
  submitError?: string;
  onCancel: () => void;
  onConfirm: (data: ValidatedActionModalConfirmData) => void;
};

export const ValidatedActionModal: React.FC<ValidatedActionModalProps> = ({
  open,
  title,
  errors,
  attentionTitle = 'Attention',
  commentLabel = 'Comment',
  commentPlaceholder = 'Enter comment',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  validating,
  disableConfirm,
  submitting,
  submitError,
  onCancel,
  onConfirm,
}) => {
  const [comment, setComment] = useState('');

  useEffect(() => {
    if (!open) {
      setComment('');
    }
  }, [open]);

  const trimmedComment = comment.trim();
  const hasErrors = errors.length > 0;
  const isConfirmDisabled =
    trimmedComment.length === 0 ||
    !!submitting ||
    !!validating ||
    !!disableConfirm ||
    hasErrors;

  const handleConfirm = () => {
    if (isConfirmDisabled) return;

    onConfirm({ comment: trimmedComment });
  };

  return (
    <Modal
      zIndex={9999}
      width={520}
      open={open}
      onCancel={onCancel}
      onOk={handleConfirm}
      okText={confirmText}
      cancelText={cancelText}
      title={title}
      maskClosable={false}
      classNames={{
        footer: 'flex items-center justify-between mt-[16px]',
      }}
      okProps={{
        type: 'primary',
        loading: submitting,
        disabled: isConfirmDisabled,
        className: twMerge(
          'min-w-[16rem] w-[168px] p-2',
          '!rounded-[16px] !border-0',
          isConfirmDisabled
            ? 'opacity-50 cursor-not-allowed !bg-vividTangelo !text-white'
            : '!bg-vividTangelo hover:!bg-dodgerBlue !text-white hover:!text-white'
        ),
      }}
      cancelProps={{
        className: twMerge(
          'min-w-[16rem] w-[168px] p-2',
          '!rounded-[16px]',
          'bg-dimGray hover:bg-waterLightHover50',
          '!text-black hover:!text-white'
        ),
      }}
    >
      <div className='py-[8px]'>
        {hasErrors && (
          <div className='rounded-[10px] border border-honoluluBlue border-opacity-40 bg-aliceBlue p-2'>
            <div className='flex items-center gap-1'>
              <Attention width={16} className='text-honoluluBlue' />
              <span className='text-[15px] font-semibold'>
                {attentionTitle}
              </span>
            </div>

            <ul className='m-0 mt-1 list-disc space-y-1 pl-4 text-[13px] font-medium'>
              {errors.map((error, index) => (
                <li key={`${index}-${error}`}>{error}</li>
              ))}
            </ul>
          </div>
        )}

        {submitError && (
          <div
            className={twMerge(
              'rounded-[10px] border border-redOrange border-opacity-40 bg-seashell p-2 text-[13px] font-medium text-redOrange',
              hasErrors ? 'mt-3' : ''
            )}
          >
            {submitError}
          </div>
        )}

        <div
          className={twMerge(
            'space-y-3',
            hasErrors || submitError ? 'mt-3' : ''
          )}
        >
          <TextArea
            label={commentLabel}
            placeholder={commentPlaceholder}
            required
            value={comment}
            onChange={e => setComment(e.target.value)}
            classNameInputWrapper='max-w-none'
            className='!resize-none !rounded-[10px]'
            rows={4}
            maxLength={700}
          />
        </div>
      </div>
    </Modal>
  );
};
